import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import { formatDateTime, MOVEMENT_LABELS } from '../lib/format';
import type { MovementEntry } from '../lib/queries';
import { Spinner } from '../components/ui';

/**
 * Empfangsbestätigung — bewusst ohne Anmeldung erreichbar.
 * Der Einmal-Link aus der E-Mail ist der Nachweis.
 */
export function ConfirmPage() {
  const params = useParams({ strict: false }) as { token?: string };
  const token = params.token ?? '';

  const movement = useQuery({
    queryKey: ['confirm', token],
    queryFn: () => api<MovementEntry>(`/confirm/${token}`),
    enabled: Boolean(token),
    retry: false,
  });

  const confirm = useMutation({
    mutationFn: () => api(`/confirm/${token}`, { method: 'POST' }),
    onSuccess: () => movement.refetch(),
  });

  return (
    <div className="grid min-h-dvh place-items-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="animate-fade-up mb-6 text-center">
          <h1 className="font-[family-name:var(--font-display)] text-3xl">Geräteübernahme</h1>
          <p className="mt-1 text-sm text-[var(--color-fg3)]">Khadi Inventarverwaltung</p>
        </div>

        <div className="surface animate-fade-up p-6">
          {movement.isLoading && <Spinner />}

          {movement.isError && (
            <p className="text-center text-sm text-[var(--color-fg2)]">
              Dieser Bestätigungslink ist nicht mehr gültig. Vermutlich wurde der Empfang bereits
              bestätigt.
            </p>
          )}

          {movement.data && (
            <>
              <dl className="mb-5 space-y-3">
                <div>
                  <dt className="eyebrow">Gerät</dt>
                  <dd className="text-[0.9375rem]">{movement.data.asset?.model_name}</dd>
                </div>
                <div>
                  <dt className="eyebrow">Inventarnummer</dt>
                  <dd className="tabular text-[0.9375rem]">{movement.data.asset?.asset_tag}</dd>
                </div>
                <div>
                  <dt className="eyebrow">Seriennummer</dt>
                  <dd className="tabular text-[0.9375rem]">{movement.data.asset?.serial}</dd>
                </div>
                <div>
                  <dt className="eyebrow">Vorgang</dt>
                  <dd className="text-[0.9375rem]">
                    {MOVEMENT_LABELS[movement.data.kind]} am{' '}
                    {formatDateTime(movement.data.occurred_at)}
                  </dd>
                </div>
              </dl>

              {movement.data.confirmed_at ? (
                <p className="flex items-center justify-center gap-2 rounded-[var(--radius-md)] bg-[var(--color-khadi-forest-100)] px-5 py-4 text-sm text-[var(--color-khadi-forest-700)]">
                  <CheckCircle2 className="h-4 w-4" aria-hidden />
                  Empfang bestätigt am {formatDateTime(movement.data.confirmed_at)}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => confirm.mutate()}
                  disabled={confirm.isPending}
                  className="btn btn-primary btn-lg w-full"
                >
                  <CheckCircle2 className="h-4 w-4" aria-hidden />
                  Erhalt bestätigen
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
